// Slice 47 — Audit screen. Read-only view over GET /admin/audit (the API
// is live since Slice 39). Action + actor filter, cursor "Load more".

import { useCallback, useEffect, useState } from "react";
import { AuditApi, type AuditRow } from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqInput, Icons } from "../../ui";
import { MonoId } from "../licenses/MonoId";

const ACTION_TONE: Record<string, "accent" | "danger" | "warn" | "success" | "neutral"> = {
  "license.issue": "accent",
  "license.revoke": "danger",
  "license.unrevoke": "warn",
  "feedback.update": "neutral",
  "feedback.reply": "accent",
  "feedback.redact": "warn",
  "customer.create": "success",
  "customer.update": "neutral",
  "customer.reveal_email": "warn",
  "customer.export": "accent",
  "customer.erase": "danger",
  "dsr.open": "warn",
  "dsr.verify": "accent",
  "dsr.fulfil": "success",
  "dsr.reject": "danger",
  "retention.update": "neutral",
  "subprocessor.add": "success",
  "subprocessor.remove": "danger",
  "auth.bootstrap": "accent",
  "auth.login": "neutral",
  "auth.logout": "neutral",
};

export function AuditScreen() {
  const { refreshTick, toast } = useShell();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Debounce the q-input so we don't issue a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await AuditApi.list({
        limit: 100,
        action: action.trim() || undefined,
        actor: actor.trim() || undefined,
        q: debouncedQ || undefined,
      });
      if (cancelled) return;
      setLoading(false);
      if (res.ok) {
        setRows(res.data.data);
        setNextCursor(res.data.page.cursor);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [action, actor, debouncedQ, refreshTick]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    const res = await AuditApi.list({
      cursor: nextCursor,
      limit: 100,
      action: action.trim() || undefined,
      actor: actor.trim() || undefined,
      q: debouncedQ || undefined,
    });
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setRows((prev) => [...prev, ...res.data.data]);
    setNextCursor(res.data.page.cursor);
  }, [nextCursor, action, actor, debouncedQ, toast]);

  const exportUrl = buildExportUrl({
    action: action.trim() || undefined,
    actor: actor.trim() || undefined,
    q: debouncedQ || undefined,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header total={rows.length} exportUrl={exportUrl} />
      <FilterBar
        action={action}
        actor={actor}
        q={q}
        onAction={setAction}
        onActor={setActor}
        onQ={setQ}
      />
      <HqCard padding={false}>
        <Table rows={rows} loading={loading} />
        {nextCursor && (
          <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
            <HqButton variant="secondary" size="sm" onClick={() => void loadMore()}>
              Load more
            </HqButton>
          </div>
        )}
      </HqCard>
    </div>
  );
}

function buildExportUrl(params: {
  action?: string | undefined;
  actor?: string | undefined;
  q?: string | undefined;
}): string {
  const sp = new URLSearchParams();
  if (params.action) sp.set("action", params.action);
  if (params.actor) sp.set("actor", params.actor);
  if (params.q) sp.set("q", params.q);
  const s = sp.toString();
  return `/admin/audit/export.csv${s ? `?${s}` : ""}`;
}

function Header({ total, exportUrl }: { total: number; exportUrl: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Audit width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Audit log
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {total} rows · append-only · 7-year retention (spec §5.2)
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <a
        href={exportUrl}
        download
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          background: "var(--hq-bg-raised)",
          border: "1px solid var(--hq-border)",
          borderRadius: "var(--hq-radius-md)",
          color: "var(--hq-text)",
          fontSize: 12.5,
          fontWeight: 500,
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        <Icons.Download width={13} height={13} sw={1.8} />
        Export CSV
      </a>
    </div>
  );
}

function FilterBar({
  action,
  actor,
  q,
  onAction,
  onActor,
  onQ,
}: {
  action: string;
  actor: string;
  q: string;
  onAction: (v: string) => void;
  onActor: (v: string) => void;
  onQ: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
      <div style={{ flex: 1, maxWidth: 280 }}>
        <HqInput
          placeholder="action (e.g. license.issue)"
          leadingIcon={Icons.Search}
          value={action}
          onChange={(e) => onAction(e.target.value)}
          mono
        />
      </div>
      <div style={{ flex: 1, maxWidth: 280 }}>
        <HqInput
          placeholder="actor (e.g. stian)"
          leadingIcon={Icons.Users}
          value={actor}
          onChange={(e) => onActor(e.target.value)}
          mono
        />
      </div>
      <div style={{ flex: 1, maxWidth: 320 }}>
        <HqInput
          placeholder="search meta + targetId (e.g. license id, customer id)"
          leadingIcon={Icons.Search}
          value={q}
          onChange={(e) => onQ(e.target.value)}
          mono
        />
      </div>
    </div>
  );
}

function Table({ rows, loading }: { rows: AuditRow[]; loading: boolean }) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          color: "var(--hq-text-dim)",
          fontSize: 12.5,
        }}
      >
        {loading ? "Loading…" : "No audit rows match this filter."}
      </div>
    );
  }
  return (
    <div style={{ maxHeight: 640, overflow: "auto" }} className="hq-scroll">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr
            style={{
              position: "sticky",
              top: 0,
              background: "var(--hq-bg-raised)",
              borderBottom: "1px solid var(--hq-border)",
              textAlign: "left",
              zIndex: 1,
            }}
          >
            <Th>Timestamp</Th>
            <Th>Actor</Th>
            <Th>Action</Th>
            <Th>Target</Th>
            <Th>IP hash</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        color: "var(--hq-text-dim)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </td>
  );
}

function Row({ row }: { row: AuditRow }) {
  const tone = ACTION_TONE[row.action] ?? "neutral";
  return (
    <tr style={{ borderBottom: "1px solid var(--hq-border)" }}>
      <td style={{ padding: "10px 14px" }}>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-muted)" }}>
          {fmtTs(row.at)}
        </span>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text)" }}>
          {row.actor}
        </span>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <HqBadge tone={tone} size="sm">
          {row.action}
        </HqBadge>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
            {row.targetType}
          </span>
          {row.targetId && <MonoId value={row.targetId} short={16} color="var(--hq-text)" />}
        </span>
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
          {row.ipHash ? `${row.ipHash.slice(0, 12)}…` : <em>cleared</em>}
        </span>
      </td>
    </tr>
  );
}

function fmtTs(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}
